<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCountryOptions } from 'dashboard/composables/useCountryOptions';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import HtmlSignaturePreview from './HtmlSignaturePreview.vue';

// { de: html, hu: html } keyed by lowercase ISO 3166 alpha-2 code, as the inbox stores it.
const props = defineProps({
  signatures: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const { countryOptionsFor } = useCountryOptions();

let lastRowId = 0;
const toRows = signatures =>
  Object.entries(signatures || {}).map(([country, html]) => {
    lastRowId += 1;
    return { id: lastRowId, country, html: html || '' };
  });
// A row whose country is not chosen yet stays here and is not saved.
const toSignatures = list =>
  Object.fromEntries(
    list.filter(row => row.country).map(row => [row.country, row.html])
  );
const sameSignatures = (a, b) => {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every(key => a[key] === b[key])
  );
};

const rows = ref(toRows(props.signatures));

// Rebuild on an outside change (another inbox loaded), not on our own update coming back.
watch(
  () => props.signatures,
  value => {
    if (!sameSignatures(value || {}, toSignatures(rows.value))) {
      rows.value = toRows(value);
    }
  }
);

const emitUpdate = () => emit('update', toSignatures(rows.value));

// A country can hold one signature, so the other rows' countries are not offered.
const optionsFor = row => countryOptionsFor(rows.value, row);

const addRow = () => {
  lastRowId += 1;
  rows.value.push({ id: lastRowId, country: '', html: '' });
};

const removeRow = row => {
  rows.value = rows.value.filter(other => other !== row);
  emitUpdate();
};

const setCountry = (row, country) => {
  row.country = country;
  emitUpdate();
};

const setHtml = (row, html) => {
  row.html = html;
  emitUpdate();
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <p v-if="!rows.length" class="mb-0 text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.EDIT.COUNTRY_SIGNATURES.EMPTY') }}
    </p>
    <div
      v-for="row in rows"
      :key="row.id"
      class="border border-n-slate-6 rounded-md p-3"
    >
      <div class="flex items-center gap-3 mb-2">
        <ComboBox
          :model-value="row.country"
          :options="optionsFor(row)"
          :placeholder="t('INBOX_MGMT.EDIT.COUNTRY_SIGNATURES.COUNTRY')"
          class="max-w-xs [&>div>button]:h-8"
          @update:model-value="country => setCountry(row, country)"
        />
        <button
          type="button"
          class="text-red-600 hover:text-red-800 text-sm ml-auto"
          @click="removeRow(row)"
        >
          {{ t('INBOX_MGMT.EDIT.COUNTRY_SIGNATURES.REMOVE') }}
        </button>
      </div>
      <HtmlSignaturePreview
        :html-signature="row.html"
        @update="html => setHtml(row, html)"
      />
    </div>
    <div>
      <NextButton
        type="button"
        icon="i-lucide-plus"
        faded
        blue
        size="sm"
        :label="t('INBOX_MGMT.EDIT.COUNTRY_SIGNATURES.ADD')"
        @click="addRow"
      />
    </div>
    <p class="mb-0 text-xs text-gray-500 dark:text-gray-400">
      {{ t('INBOX_MGMT.EDIT.COUNTRY_SIGNATURES.HELP') }}
    </p>
  </div>
</template>
